import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { FichaTP, clpTP } from "./TPValidar";

const errTxt = (e, d) => { const x = e?.response?.data?.detail; return typeof x === "string" ? x : d; };

/** Caja: pago (abono) de la deuda de Tarjeta Propia con un solo medio de pago de la caja. */
export default function TPAbonoDialog({ open, onClose, nombre, metodos, shift, terminalId, cobrarEnEquipo, onDone }) {
  const [codigo, setCodigo] = useState("");
  const [cuenta, setCuenta] = useState(null);
  const [monto, setMonto] = useState("");
  const [recibido, setRecibido] = useState("");
  const [metodo, setMetodo] = useState("EFECTIVO");
  const [busy, setBusy] = useState(false);
  const cerrar = () => { setCodigo(""); setCuenta(null); setMonto(""); setRecibido(""); setMetodo("EFECTIVO"); onClose(); };
  const buscar = async () => {
    if (!codigo.trim()) return;
    setBusy(true);
    try { const { data } = await api.get("/tarjeta-propia/consulta", { params: { codigo: codigo.trim() } }); setCuenta(data); setMonto(String(Math.round(data.deuda))); }
    catch (e) { toast.error(errTxt(e, "Tarjeta no encontrada")); setCuenta(null); }
    setBusy(false);
  };
  const m = Number(monto || 0);
  const esCash = metodo === "EFECTIVO";
  const pagar = async () => {
    if (!(m > 0)) { toast.error("Escribe el monto a abonar"); return; }
    if (m > cuenta.deuda + 0.5) { toast.error(`El abono supera la deuda de ${clpTP(cuenta.deuda)}`); return; }
    if (esCash && Number(recibido || m) < m) { toast.error("El efectivo recibido no alcanza"); return; }
    setBusy(true);
    try {
      const equipo = cobrarEnEquipo ? await cobrarEnEquipo(metodo, m) : null;
      if (equipo === false) { setBusy(false); return; }
      const { data } = await api.post("/tarjeta-propia/abonos", {
        account_id: cuenta.account_id, monto: m, payment_method: metodo, shift_id: shift.shift_id,
        terminal_id: terminalId || undefined, monto_recibido: esCash ? Number(recibido || m) : undefined,
        ...(equipo?.tbk ? { tbk: equipo.tbk } : {}),
      });
      toast.success(`Abono de ${clpTP(m)} registrado · disponible ${clpTP(data.disponible_despues)}`);
      onDone?.(data);
      cerrar();
    } catch (e) { toast.error(errTxt(e, "No se pudo registrar el abono")); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && cerrar()}>
      <DialogContent className="w-[calc(100%-1.5rem)] sm:w-full max-w-md rounded-3xl" data-testid="tp-abono-dialog" aria-describedby={undefined}>
        <DialogHeader><DialogTitle>Pago {nombre}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="flex gap-2">
            <Input autoFocus value={codigo} onChange={(e) => setCodigo(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.stopPropagation(); buscar(); } }}
                   placeholder="Escanea el QR o escribe el RUT" data-testid="tp-abono-codigo" className="h-11 rounded-xl font-mono-num" />
            <Button onClick={buscar} disabled={busy} data-testid="tp-abono-buscar" className="h-11 rounded-xl bg-slate-900 text-white">Buscar</Button>
          </div>
          {cuenta && (
            <>
              <FichaTP r={cuenta} />
              {cuenta.deuda <= 0 ? (
                <p className="text-sm text-emerald-700 font-semibold" data-testid="tp-abono-sin-deuda">El cliente no tiene deuda pendiente.</p>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-2">
                    <label className="text-xs font-semibold text-slate-600">Monto a abonar
                      <Input inputMode="numeric" value={monto} onChange={(e) => setMonto(e.target.value.replace(/\D/g, ""))} data-testid="tp-abono-monto" className="mt-1 h-11 rounded-xl font-mono-num text-lg" />
                    </label>
                    <div className="flex items-end">
                      <Button type="button" variant="outline" onClick={() => setMonto(String(Math.round(cuenta.deuda)))} data-testid="tp-abono-saldar" className="w-full h-11 rounded-xl">Saldar total</Button>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-slate-600 mb-1">Paga con (un solo medio)</p>
                    <div className="grid grid-cols-2 gap-2">
                      {metodos.map((pm) => (
                        <button key={pm.codigo} type="button" onClick={() => setMetodo(pm.codigo)} data-testid={`tp-abono-metodo-${pm.codigo.toLowerCase()}`}
                                className={`h-10 rounded-xl border text-sm font-semibold transition-colors ${metodo === pm.codigo ? "border-sky-500 bg-sky-50 text-sky-800" : "border-slate-200 hover:border-slate-300"}`}>
                          {pm.nombre}
                        </button>
                      ))}
                    </div>
                  </div>
                  {esCash && (
                    <label className="block text-xs font-semibold text-slate-600">Efectivo recibido
                      <Input inputMode="numeric" value={recibido} placeholder={String(m || "")} onChange={(e) => setRecibido(e.target.value.replace(/\D/g, ""))} data-testid="tp-abono-recibido" className="mt-1 h-11 rounded-xl font-mono-num" />
                      {Number(recibido) > m && <span className="block mt-1 text-emerald-700 font-bold" data-testid="tp-abono-vuelto">Vuelto {clpTP(Number(recibido) - m)}</span>}
                    </label>
                  )}
                  <Button onClick={pagar} disabled={busy} data-testid="tp-abono-confirmar" className="w-full h-12 rounded-xl bg-sky-600 hover:bg-sky-700 text-white font-bold">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : `Registrar abono ${clpTP(m)}`}
                  </Button>
                </>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
